import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useHistory, useParams } from "react-router-dom";
import { withTranslation } from 'react-i18next';
import { ProgressBar } from "primereact/progressbar";
import { Panel } from 'primereact/panel';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { Button } from 'primereact/button';
import axios from "axios";
import moment from "moment";

import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import huLocale from '@fullcalendar/core/locales/hu';

const CalendarComponent = (props) => {
  const { t, onDetailedSearchActiveChange } = props;
  const history = useHistory();
  const { entity, id } = useParams();
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const calendarRef = useRef(null);
  const [employees, setEmployees] = useState([]);
  const [categories, setCategories] = useState([]);
  //detailed search
  const [detailedSearchQuery, setDetailedSearchQuery] = useState('');
  const isDetailedSearchActive = React.useMemo(() => {
    return (detailedSearchQuery || '').toString().trim().length > 0;
  }, [detailedSearchQuery]);
  // const [dsEmployee, setDsEmployee] = useState(null);
  const [dsEmployee, setDsEmployee] = useState([]);
  const [dsCategory, setDsCategory] = useState(null);

  const apiCall = (method, url, data) => {
    return axios({
      method,
      url: process.env.REACT_APP_API_HOST + url,
      data,
      params: {},
      headers: {
        'Authorization': 'Bearer ' + localStorage.getItem("userToken"),
        'App-Network': localStorage.getItem("networkId")
      }
    });
  };

  const workdayExceptions = {
  };

  const calendarOptions = useMemo(() => ({
    plugins: [dayGridPlugin, interactionPlugin],
    initialView: 'dayGridMonth',
    headerToolbar: {
        left: 'prev,next today',
        center: 'title',
        right: 'dayGridMonth,dayGridWeek,dayGridDay'
    },
    locales: [huLocale],
    locale: 'hu',
    selectable: true,
    selectMirror: true,
    unselectAuto: true,
    dayCellClassNames: (arg) => {
      const dateStr = moment(arg.date).format('YYYY-MM-DD');
      const dayOfWeek = arg.date.getDay();
      if (workdayExceptions[dateStr]) {
        return workdayExceptions[dateStr] === 'WORK' ? ['fc-day-working'] : ['fc-day-non-working'];
      }
      if (dayOfWeek === 0 || dayOfWeek === 6) {
        return ['fc-day-non-working'];
      }
      return ['fc-day-working'];
    },
    longPressDelay: 150,
    selectLongPressDelay: 150,
    selectMinDistance: 0,
    dayMaxEventRows: false,
    expandRows: true,
    height: 'auto',
    displayEventTime: false,
    }), []);

    const handleSelect = (info) => {
    // const start = moment(info.startStr).format('YYYY-MM-DD');
    // const endInclusive = moment(info.endStr).subtract(1, 'day').format('YYYY-MM-DD');
    const startDate = info.start ?? (info.startStr ? new Date(info.startStr) : null);
    const endDateExcl = info.end ?? (info.endStr ? new Date(info.endStr) : null);
    if (!startDate || !endDateExcl) return;
    const start = moment(startDate).format('YYYY-MM-DD');
    const endInclusive = moment(endDateExcl).subtract(1, 'day').format('YYYY-MM-DD');

    if (entity === 'calendar') {
        history.push(`/hr/calendar/create?start=${start}&end=${endInclusive}`);
    } else if (entity === 'employee') {
        history.push(`/hr/employee/read/${id}?create=calendar&employeeId=${id}&start=${start}&end=${endInclusive}`);
        }
    };

  const buildDetailedSearchQuery = () => {
    let parts = [];
    // if (dsEmployee) {
    //   parts.push(`search[employee][operator]==&search[employee][value]=${dsEmployee}`)
    // }
    if (dsEmployee && dsEmployee.length > 0) {
      parts.push(`search[employee][operator]=in&search[employee][value]=${dsEmployee.join(',')}`);
    }
    if (dsCategory) {
      parts.push(`search[hr_calendar_category][operator]==&search[hr_calendar_category][value]=${dsCategory}`)
    }
    return parts.filter(Boolean).join('&');
  }

  useEffect(() => {
    apiCall('get', '/api/v2/employee?rows=9999')
      .then(response => setEmployees(response.data?.data?.items || []))
      .catch(error => {
        window.App.toastShow('error', t('error'), error.toString(), '', error.response);
      })
      .finally(() => setLoading(false));
    apiCall('get', '/api/v2/hr-calendar-category?rows=9999')
      // .then(response => {
      //   const raw = response?.data?.data || [];
      //   setCategories(raw.map(item => ({ id: item.key, name: item.value })));
      // });
      .then(response => setCategories(response.data?.data?.items || []))
      .catch(error => {
        window.App.toastShow('error', t('error'), error.toString(), '', error.response);
      })
      .finally(() => setLoading(false));
  }, []);


  useEffect(() => {
    setLoading(true);
    let query = '';
    if (entity === 'employee' && id) {
      query = '?search[employee][operator]=%3D&search[employee][value]=' + id;
    } else if (detailedSearchQuery) {
      query = '?' + detailedSearchQuery;
    }
    apiCall('get', '/api/v2/hr-calendar' + query)
      .then(response => {
        const items = response?.data?.data?.items || [];
        const mapped = items.map(item => {
          const start = moment(item.start_date, 'YYYY-MM-DD').format('YYYY-MM-DD');
          const endExclusive = moment(item.end_date, 'YYYY-MM-DD').add(1, 'day').format('YYYY-MM-DD');

          const acceptedPrefix = item.accepted === true ? '' : '(!) ';
          const employeeName = item?.employee?.name || '';
          const categoryName = item?.hr_calendar_category?.name || '';
          const notePart = item.note ? ` (${item.note})` : '';
          // const title = `${acceptedPrefix}Kategória: ${item?.hr_calendar_category?.name || ''}  Megjegyzés: ${item.note || '-'}`;
          const title = `${acceptedPrefix}${employeeName}: ${categoryName}${notePart}`;

          return {
            id: String(item.id),
            title,
            start,
            end: endExclusive,
            allDay: true,
            backgroundColor: item?.hr_calendar_category?.color || '#000000',
            borderColor: item?.hr_calendar_category?.color || '#000000',
            textColor: '#ffffff',
            extendedProps: {
              employeeId: item?.employee?.id || 0,
              accepted: item?.accepted === true
            }
          };
        });
        setEvents(mapped);
      })
      .catch(error => {
        window.App.toastShow('error', t('error'), error.toString(), '', error.response);
      })
      .finally(() => setLoading(false));
  }, [entity, id, t, window.App.state?.rerenderCalendar, detailedSearchQuery]);

  useEffect(() => {
    if (!loading && calendarRef.current) {
        const api = calendarRef.current.getApi();
        setTimeout(() => api.updateSize(), 200);
    }
  }, [loading]);

  useEffect(() => {
    if (typeof onDetailedSearchActiveChange === 'function') {
      onDetailedSearchActiveChange(isDetailedSearchActive);
    }
  }, [isDetailedSearchActive, onDetailedSearchActiveChange]);


  const handleEventClick = (clickInfo) => {
    const eventId = clickInfo?.event?.id;
    if (!eventId) return;

    if (entity === 'calendar') {
      history.push('/hr/calendar/update/' + eventId);
    }
    if (entity === 'employee') {
      history.push('/hr/employee/read/' + id + '?update=calendar&calendarId=' + eventId);
    }
  };

//   const handleDateClickOrSelect = (arg) => {
//     if (arg.startStr && arg.endStr) {
//       const start = moment(arg.startStr).format('YYYY-MM-DD');
//       const endInclusive = moment(arg.endStr).subtract(1, 'day').format('YYYY-MM-DD');
//       if (entity === 'calendar') {
//         history.push('/hr/calendar/create' + `?start=${start}&end=${endInclusive}`);
//       }
//       if (entity === 'employee') {
//         history.push('/hr/employee/read/' + id + `?create=calendar&employeeId=${id}&start=${start}&end=${endInclusive}`);
//       }
//       return;
//     }

//     const day = arg?.dateStr || moment(arg?.date).format('YYYY-MM-DD');
//     if (entity === 'calendar') {
//       history.push('/hr/calendar/create' + '?day=' + day);
//     }
//     if (entity === 'employee') {
//       history.push('/hr/employee/read/' + id + '?create=calendar&day=' + day);
//     }
//   };

  return (
    <div className="hr-calendar widget">
      {loading && (
        <ProgressBar
          mode="indeterminate"
          style={{ height: '5px', position: "absolute", top: 0, left: 0, borderRadius: 0 }}
        />
      )}
      {props.detailedSearchVisible && (
        <React.Fragment>
        <Panel className="p-mb-2">
          <div className={"p-grid"}>
            <div className={"p-col-12 p-lg-4"}>
              <div className="p-field p-fluid">
                <label>{t('columnEmployee')}</label>
                <MultiSelect 
                  value={dsEmployee} 
                  options={employees} 
                  onChange={(e) => setDsEmployee(e.value || [])}
                  optionLabel="name" 
                  optionValue="id"
                  placeholder={t('dsSelect')}
                  showClear
                  display="chip"
                  filter
                  appendTo={document.body}
                />
              </div>
            </div>
            <div className={"p-col-12 p-lg-4"}>
              <div className="p-field p-fluid">
                <label>{t('columnCategory')}</label>
                <Dropdown 
                  value={dsCategory} 
                  options={categories} 
                  onChange={(e) => setDsCategory(e.value)} 
                  optionLabel="name" 
                  optionValue="id"
                  placeholder={t('dsSelect')}
                  showClear
                />
              </div>
            </div>
            <div className={"p-col-12 p-text-right"}>
              <Button
                type="button"
                label={t('dsClearCriteria')}
                icon="pi pi-times"
                className="p-button-danger p-mr-2"
                onClick={() => {
                  setDsEmployee([]);
                  setDsCategory(null);
                  setDetailedSearchQuery('');
                }}
              />
              <Button
                type="button"
                label={t('dsSearch')}
                icon="pi pi-search"
                className="p-button-primary"
                onClick={() => setDetailedSearchQuery(buildDetailedSearchQuery())}
              />
            </div>
          </div>
        </Panel>
        {/* <hr className="p-mb-4" style={{ border: 'none', borderTop: '4px solid #000000' }} /> */}
        <div className="p-col-12 p-my-3"></div>
        </React.Fragment>
      )}

      <FullCalendar
        ref={calendarRef}
        {...calendarOptions}
        events={events}
        initialDate={moment().format('YYYY-MM-DD')}
        // dateClick={handleDateClickOrSelect}
        select={handleSelect}
        eventClick={handleEventClick}
      />
    </div>
  );
};

export default withTranslation('common')(CalendarComponent);
